"use client";

import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormField,
} from "@/components/ui/form";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AlertMsg from "@/components/dialogs/common/AlertMsg";
import { resetPasswordSchema } from "@/utils/schema/resetPasswordSchema";
import { useResetPasswordMutation } from "@/store/slices/apiSlice";
import { SHOW_MESSAGE } from "@/lib/constant/constant";
import { Theme_logo } from "@/lib/Images";

const ResetPassword = () => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const [alertMsg, setAlertMsg] = useState({
    open: false,
    label: "",
    msg: "",
    isError: false,
    isLoading: false,
    btnText: "",
    canClose: false,
  });

  const form = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  const handleAlertClose = () => {
    setAlertMsg({
      open: false,
      label: "",
      msg: "",
      isError: false,
      isLoading: false,
      btnText: "",
      canClose: false,
    });

    if (alertMsg?.btnText === "Go to login") {
      localStorage.clear();
      navigate("/login", { replace: true });
    }
  };

  useEffect(() => {
    if (!searchParams.get("token")) {
      localStorage.clear();
      navigate("/login", { replace: true });
    }
  }, [searchParams, navigate]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        token: searchParams.get("token"),
        new_password: data.newPassword,
      };

      // const res = await resetPassword(payload).unwrap();
      const res = {
        response: {
          statusEnum: 1,
        },
      };

      if (res.response.statusEnum === 1) {
        setAlertMsg({
          open: true,
          label: SHOW_MESSAGE?.RESET_PASSWORD_SUCCESS_HEADING,
          msg: SHOW_MESSAGE?.RESET_PASSWORD_SUCCESS_MSG,
          isError: false,
          btnText: "Go to login",
          canClose: false,
        });
        form.reset();
      } else if (res?.response.errorCode === 222) {
        setAlertMsg({
          open: true,
          label: SHOW_MESSAGE?.VALIDATION_FAILED,
          msg: res?.response.message,
          isError: true,
          btnText: "Close",
          canClose: false,
        });
      } else {
        setAlertMsg({
          open: true,
          label: SHOW_MESSAGE?.RESET_PASSWORD_FAILED,
          msg: res?.response.message,
          isError: true,
          btnText: "Close",
          canClose: false,
        });
      }
    } catch (error) {
      console.log("Error", error);
      setAlertMsg({
        open: true,
        label: SHOW_MESSAGE?.RESET_PASSWORD_FAILED,
        msg:
          error?.data?.response?.message || SHOW_MESSAGE?.SOMETHING_WENT_WRONG,
        isError: true,
        btnText: "Close",
        canClose: false,
      });
    }
  };

  return (
    <div className="w-full max-w-xl px-4">
      <div className="text-center mb-10">
        <div className="flex justify-center mb-16">
          <img
            src={Theme_logo || "/placeholder.svg"}
            className="h-20"
            alt="Simstac Systems Logo"
          />
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-4">
          Create a new password
        </h1>

        <p className="text-responsive-sm text-foreground/80">
          Enter a new password below to secure your account.
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 max-w-md mx-auto"
        >
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="sr-only">New Password</FormLabel>

                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      className="w-full h-12 pr-11 bg-white text-black"
                      error={fieldState.error}
                    />

                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-0"
                    >
                      {showNewPassword ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeOff className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>

                <FormMessage className="text-red-500 text-sm mt-1" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="sr-only">Confirm Password</FormLabel>

                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter new password"
                      className="w-full h-12 pr-11 bg-white text-black"
                      error={fieldState.error}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-0"
                    >
                      {showConfirmPassword ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeOff className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>

                <FormMessage className="text-red-500 text-sm mt-1" />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full h-12 !mt-10"
            disabled={isLoading}
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm font-semibold text-[#1E9BFF] hover:underline"
            >
              Back to Login
            </Link>
          </div>
        </form>
      </Form>

      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />
    </div>
  );
};

export default ResetPassword;
